// Panel lateral con las solicitudes que el usuario actual debe aprobar/rechazar.
// Se abre desde el icono en el header. Funciona para director y autoridad (RRHH usa la sección Personal).

import React, { useEffect, useState, useCallback } from 'react';
import {
    Drawer, Box, Typography, IconButton, Chip, Button,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Paper, Tooltip, Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DescriptionIcon from '@mui/icons-material/Description';
import AdjuntosSolicitud from './AdjuntosSolicitud';
import { nodeApiPath } from '../utils/paths';
import { triggerSessionExpired } from '../api/endpoints';

const BR = '4px';

async function jf(path, options = {}) {
    const token = localStorage.getItem('SIGIF_JWT_TOKEN');
    if (!token) { triggerSessionExpired(); throw new Error('No hay token'); }
    const res = await fetch(nodeApiPath(path), {
        ...options,
        headers: {
            'Accept': 'application/json',
            ...(options.body && !(options.body instanceof FormData) ? { 'Content-Type': 'application/json' } : {}),
            'Authorization': `Bearer ${token}`,
            ...(options.headers || {}),
        },
    });
    if (res.status === 401) { triggerSessionExpired(); throw new Error('Token expirado'); }
    return await res.json();
}

const fmtDate = (d) => { if (!d) return '-'; return new Date(d).toLocaleDateString('es-AR'); };

const rolColors = {
    DIRECTOR: { bg: '#e3f2fd', color: '#1565c0', label: 'Como Director' },
    AUTORIDAD: { bg: '#f3e5f5', color: '#6a1b9a', label: 'Como Autoridad' },
};

const rolLabelAvanzar = {
    DIRECTOR: 'Aprobar',
    AUTORIDAD: 'Finalizar',
    DIR_ADMIN: 'Dar visto bueno',
    JURIDICO: 'Enviar dictamen',
};

// Componente inline: muestra resumen del año del empleado (para el aprobador)
function ResumenEmpleadoInline({ idPersonal }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!idPersonal || !open || data) return;
        jf(`personal/resumen-ano/${idPersonal}`).then(r => { if (r.ok) setData(r); }).catch(() => { });
    }, [idPersonal, open, data]);

    if (!idPersonal) return null;

    const totLic = data ? data.licencias.reduce((acc, l) => acc + l.Dias, 0) : 0;
    const totTard = data?.asistencia?.Tardanzas || 0;
    const totAus = data?.asistencia?.Ausencias || 0;
    const horas = Math.floor((data?.asistencia?.MinTardanza || 0) / 60);
    const min = (data?.asistencia?.MinTardanza || 0) % 60;

    return (
        <Box sx={{ mt: 1, p: 0.5 }}>
            <Button size="small" onClick={() => setOpen(o => !o)} sx={{ textTransform: 'none', fontSize: '0.7rem', color: '#666', minHeight: 0, py: 0 }}>
                {open ? '▼' : '▶'} Ver antecedentes del año
            </Button>
            {open && (
                <Box sx={{ mt: 0.5, p: 1, bgcolor: '#fafafa', borderRadius: BR, border: '1px solid #e0e0e0' }}>
                    {!data ? (
                        <Typography variant="caption" sx={{ color: '#999' }}>Cargando...</Typography>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Chip size="small" label={`${totLic} días de licencia`} sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontSize: '0.65rem' }} />
                            <Chip size="small" label={`${totTard} tardanzas (${horas}h ${min}m)`} sx={{ bgcolor: '#fff3e0', color: '#e65100', fontSize: '0.65rem' }} />
                            <Chip size="small" label={`${totAus} ausencias`} sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '0.65rem' }} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default function MisTareasPanel({ open, onClose, onChange }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rechazarDlg, setRechazarDlg] = useState({ open: false, id: null, motivo: '' });
    const [detalleDlg, setDetalleDlg] = useState({ open: false, sol: null, trail: [] });
    const [adjDlg, setAdjDlg] = useState({ open: false, solicitud: null });
    const [notify, setNotify] = useState({ open: false, msg: '', sev: 'success' });

    const cargar = useCallback(async () => {
        setLoading(true);
        try {
            const r = await jf('personal/bandeja/mis-tareas');
            setItems(r.ok ? (r.items || []) : []);
        } catch { setItems([]); }
        setLoading(false);
    }, []);

    useEffect(() => { if (open) cargar(); }, [open, cargar]);

    const avanzar = async (id) => {
        setSaving(true);
        try {
            const r = await jf(`personal/solicitudes/${id}/avanzar`, { method: 'POST', body: JSON.stringify({}) });
            if (r.ok) {
                setNotify({ open: true, msg: r.disposicion ? `OK — Disposición ${r.disposicion}` : 'Solicitud aprobada', sev: 'success' });
                await cargar();
                onChange?.();
            } else {
                setNotify({ open: true, msg: r.mensaje || 'Error', sev: 'error' });
            }
        } catch (e) {
            setNotify({ open: true, msg: 'Error: ' + e.message, sev: 'error' });
        }
        setSaving(false);
    };

    const rechazar = async () => {
        if (!rechazarDlg.motivo.trim()) { setNotify({ open: true, msg: 'Indicá un motivo', sev: 'warning' }); return; }
        setSaving(true);
        try {
            const r = await jf(`personal/solicitudes/${rechazarDlg.id}/rechazar-v2`, {
                method: 'POST', body: JSON.stringify({ motivo: rechazarDlg.motivo }),
            });
            if (r.ok) {
                setNotify({ open: true, msg: 'Rechazada', sev: 'info' });
                setRechazarDlg({ open: false, id: null, motivo: '' });
                await cargar();
                onChange?.();
            } else {
                setNotify({ open: true, msg: r.mensaje || 'Error', sev: 'error' });
            }
        } catch (e) {
            setNotify({ open: true, msg: 'Error: ' + e.message, sev: 'error' });
        }
        setSaving(false);
    };

    const abrirDetalle = async (sol) => {
        try {
            const r = await jf(`personal/solicitudes/${sol.Id}/trail`);
            setDetalleDlg({ open: true, sol, trail: r.ok ? (r.trail || []) : [] });
        } catch { setDetalleDlg({ open: true, sol, trail: [] }); }
    };

    // Agrupar por rol
    const porRol = items.reduce((acc, it) => {
        (acc[it.Mi_Rol] = acc[it.Mi_Rol] || []).push(it);
        return acc;
    }, {});

    return (
        <>
            <Drawer anchor="right" open={open} onClose={onClose}
                PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, maxWidth: '95vw' } }}>
                <Box sx={{ p: 2, bgcolor: '#1769aa', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentTurnedInIcon />
                    <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>Mis Tareas Pendientes</Typography>
                    <Tooltip title="Refrescar"><IconButton onClick={cargar} sx={{ color: '#fff' }}><RefreshIcon /></IconButton></Tooltip>
                    <IconButton onClick={onClose} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
                </Box>

                <Box sx={{ p: 2, overflow: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                    ) : items.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6, color: '#999' }}>
                            <CheckCircleIcon sx={{ fontSize: 48, color: '#4caf50', mb: 1 }} />
                            <Typography variant="body1">No tenés tareas pendientes</Typography>
                        </Box>
                    ) : Object.entries(porRol).map(([rol, lista]) => {
                        const rc = rolColors[rol] || { bg: '#f5f5f5', color: '#333', label: rol };
                        return (
                            <Box key={rol} sx={{ mb: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Chip label={rc.label} sx={{ bgcolor: rc.bg, color: rc.color, fontWeight: 600 }} size="small" />
                                    <Typography variant="caption" sx={{ color: '#666' }}>{lista.length} solicitud{lista.length !== 1 ? 'es' : ''}</Typography>
                                </Box>
                                {lista.map(s => (
                                    <Paper key={s.Id} sx={{ p: 1.5, mb: 1, borderRadius: BR, borderLeft: `4px solid ${rc.color}` }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                            <Box>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.Apellido}, {s.Nombre}</Typography>
                                                <Typography variant="caption" sx={{ color: '#666' }}>{s.Area_Empleado || '—'}</Typography>
                                            </Box>
                                            <Chip label={s.Tipo} size="small" sx={{
                                                bgcolor: s.Tipo === 'VACACIONES' ? '#e0f2f1' : s.Tipo === 'ART42' ? '#f3e5f5' : '#e3f2fd',
                                                color: s.Tipo === 'VACACIONES' ? '#00695c' : s.Tipo === 'ART42' ? '#6a1b9a' : '#1565c0',
                                                fontSize: '0.65rem',
                                            }} />
                                        </Box>
                                        <Typography variant="caption" sx={{ display: 'block', color: '#555' }}>
                                            <strong>{fmtDate(s.Fecha_Desde)}</strong> a <strong>{fmtDate(s.Fecha_Hasta)}</strong> — {s.Dias_Habiles} días hábiles
                                        </Typography>
                                        {s.Articulo && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5, fontSize: '0.7rem' }}>
                                                {(s.Articulo || '').replace(/[\r\n\t]+/g, ' ').substring(0, 120)}{s.Articulo?.length > 120 ? '…' : ''}
                                            </Typography>
                                        )}
                                        {s.Observaciones && (
                                            <Typography variant="caption" sx={{ display: 'block', color: '#666', mt: 0.5, fontStyle: 'italic' }}>
                                                "{s.Observaciones}"
                                            </Typography>
                                        )}
                                        {rol === 'AUTORIDAD' && s.Disp_Numero && (
                                            <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="caption" sx={{ color: rc.color, fontWeight: 600 }}>
                                                    Disposición {String(s.Disp_Numero).padStart(3, '0')}-SG-{s.Disp_Ano} (BORRADOR)
                                                </Typography>
                                                <Button size="small" onClick={async () => {
                                                    try {
                                                        const r = await jf(`personal/disposiciones/${s.Id_Disposicion}`);
                                                        if (r.ok && r.disposicion?.Contenido_HTML) {
                                                            const w = window.open('', '_blank');
                                                            w.document.write(`<html><head><title>${r.disposicion.Formato}</title><style>body{font-family:Times,serif;max-width:800px;margin:30px auto;padding:20px}</style></head><body>${r.disposicion.Contenido_HTML}</body></html>`);
                                                            w.document.close();
                                                        }
                                                    } catch { /* silent */ }
                                                }} sx={{ textTransform: 'none', fontSize: '0.7rem', minHeight: 0, py: 0 }}>
                                                    Ver escrito
                                                </Button>
                                            </Box>
                                        )}
                                        {/* Historial del empleado — resumen del año */}
                                        <ResumenEmpleadoInline idPersonal={s.Id_Personal} />

                                        {rol === 'JURIDICO' && (
                                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#fff3e0', borderRadius: BR, border: '1px solid #ffe0b2' }}>
                                                <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 1 }}>
                                                    Adjuntá el dictamen antes de avanzar:
                                                </Typography>
                                                <Button size="small" variant="outlined" startIcon={<AttachFileIcon />}
                                                    onClick={() => setAdjDlg({ open: true, solicitud: s })}
                                                    sx={{ textTransform: 'none', borderColor: '#e65100', color: '#e65100', borderRadius: BR }}>
                                                    Gestionar adjuntos / Subir dictamen
                                                </Button>
                                            </Box>
                                        )}
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                            <Button size="small" variant="contained" startIcon={<CheckCircleIcon />}
                                                onClick={() => avanzar(s.Id)} disabled={saving}
                                                sx={{ bgcolor: rc.color, textTransform: 'none', borderRadius: BR, '&:hover': { bgcolor: rc.color, filter: 'brightness(0.9)' } }}>
                                                {rolLabelAvanzar[rol] || 'Avanzar'}
                                            </Button>
                                            <Button size="small" variant="outlined" startIcon={<CancelIcon />}
                                                onClick={() => setRechazarDlg({ open: true, id: s.Id, motivo: '' })} disabled={saving}
                                                sx={{ borderColor: '#c62828', color: '#c62828', textTransform: 'none', borderRadius: BR }}>
                                                Rechazar
                                            </Button>
                                            <Box sx={{ ml: 'auto', display: 'flex', gap: 0.25 }}>
                                                {(s.Tipo === 'LICENCIA' || s.Tipo === 'CARTA_MEDICA') && (
                                                    <Tooltip title="Ver adjuntos">
                                                        <IconButton size="small" onClick={() => setAdjDlg({ open: true, solicitud: s })} sx={{ color: '#1565c0' }}>
                                                            <AttachFileIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                {s.Id_Disposicion && (
                                                    <Tooltip title="Ver escrito de disposición">
                                                        <IconButton size="small" onClick={async () => {
                                                            try {
                                                                const r = await jf(`personal/disposiciones/${s.Id_Disposicion}`);
                                                                if (r.ok && r.disposicion?.Contenido_HTML) {
                                                                    const w = window.open('', '_blank');
                                                                    w.document.write(`<html><head><title>${r.disposicion.Formato}</title><style>body{font-family:Times,serif;max-width:800px;margin:30px auto;padding:20px}</style></head><body>${r.disposicion.Contenido_HTML}</body></html>`);
                                                                    w.document.close();
                                                                }
                                                            } catch { }
                                                        }} sx={{ color: '#6a1b9a' }}>
                                                            <DescriptionIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Ver historial">
                                                    <IconButton size="small" onClick={() => abrirDetalle(s)}>
                                                        <HistoryIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        );
                    })}
                </Box>
            </Drawer>

            {/* Dialog adjuntos */}
            <AdjuntosSolicitud
                open={adjDlg.open}
                idSolicitud={adjDlg.solicitud?.Id}
                solicitud={adjDlg.solicitud}
                onClose={() => setAdjDlg({ open: false, solicitud: null })}
                puedeSubir={true}
                rolUsuario={adjDlg.solicitud?.Mi_Rol || 'EMPLEADO'}
                notify={(msg, sev) => setNotify({ open: true, msg, sev })}
            />

            {/* Dialog rechazar */}
            <Dialog open={rechazarDlg.open} onClose={() => setRechazarDlg({ open: false, id: null, motivo: '' })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#ffebee', color: '#c62828', fontSize: '1rem', fontWeight: 700 }}>Rechazar solicitud</DialogTitle>
                <DialogContent sx={{ pt: 2, mt: 1 }}>
                    <TextField label="Motivo del rechazo" multiline rows={4} fullWidth required
                        value={rechazarDlg.motivo}
                        onChange={e => setRechazarDlg(p => ({ ...p, motivo: e.target.value }))}
                        sx={{ mt: 1 }} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRechazarDlg({ open: false, id: null, motivo: '' })} sx={{ textTransform: 'none' }} disabled={saving}>Cancelar</Button>
                    <Button onClick={rechazar} variant="contained" color="error" disabled={saving || !rechazarDlg.motivo.trim()}
                        sx={{ textTransform: 'none', borderRadius: BR }}>
                        {saving ? 'Rechazando...' : 'Rechazar'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog detalle con trail */}
            <Dialog open={detalleDlg.open} onClose={() => setDetalleDlg({ open: false, sol: null, trail: [] })} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: BR } }}>
                <DialogTitle sx={{ bgcolor: '#1769aa', color: '#fff', fontSize: '1rem', fontWeight: 700 }}>
                    Historial — {detalleDlg.sol?.Apellido}, {detalleDlg.sol?.Nombre}
                </DialogTitle>
                <DialogContent sx={{ pt: 2, mt: 1 }}>
                    {detalleDlg.trail.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#999', py: 2, textAlign: 'center' }}>Sin eventos</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead><TableRow>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Fecha</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Acción</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Rol</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Estado</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Usuario</TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Obs.</TableCell>
                            </TableRow></TableHead>
                            <TableBody>
                                {detalleDlg.trail.map(t => (
                                    <TableRow key={t.Id}>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{new Date(t.Fecha).toLocaleString('es-AR')}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Accion}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Rol || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>{t.Estado_Anterior ? `${t.Estado_Anterior} → ` : ''}<strong>{t.Estado_Nuevo}</strong></TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem' }}>{t.Usuario || '—'}</TableCell>
                                        <TableCell sx={{ fontSize: '0.72rem', color: '#666' }}>{t.Observacion || '—'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDetalleDlg({ open: false, sol: null, trail: [] })} sx={{ textTransform: 'none' }}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar-like mínimo */}
            {notify.open && (
                <Box sx={{ position: 'fixed', bottom: 20, right: 20, bgcolor: notify.sev === 'error' ? '#c62828' : notify.sev === 'warning' ? '#e65100' : '#2e7d32', color: '#fff', px: 2, py: 1, borderRadius: BR, zIndex: 9999, maxWidth: 400 }} onClick={() => setNotify({ open: false, msg: '' })}>
                    {notify.msg}
                    <IconButton size="small" sx={{ ml: 1, color: '#fff' }} onClick={() => setNotify({ open: false, msg: '' })}><CloseIcon fontSize="small" /></IconButton>
                </Box>
            )}
        </>
    );
}
